import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminStockGroups() {
  const [groups,  setGroups]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(false);
  const [files,   setFiles]   = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/stock-groups/admin')
      .then(r => setGroups(r.data.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openUpload = () => {
    setFiles([]);
    setModal(true);
  };

  const handleUpload = async () => {
    if (!files.length) { toast.error('Select at least one .xlsx file.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await api.post('/stock-groups/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (r.data.errors?.length) {
        r.data.errors.forEach(e => toast.error(`${e.file}: ${e.message}`));
      }
      if (r.data.data?.length) {
        toast.success(`${r.data.data.length} group(s) updated.`);
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}" from the public stock page?`)) return;
    try {
      await api.delete(`/stock-groups/${id}`);
      setGroups(gs => gs.filter(g => g._id !== id));
      toast.success('Deleted.');
    } catch { toast.error('Failed to delete.'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Current Stock (Excel Upload)</h1>
          <p className="font-body text-sm text-secondary mt-1">
            Upload Tally "Stock Group Summary" exports. Each file updates only its own group — others are untouched.
          </p>
        </div>
        <button onClick={openUpload} className="btn-primary">
          <span className="material-symbols-outlined">upload_file</span> Upload Excel File(s)
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 skeleton" />)}
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-20">
          <span className="material-symbols-outlined text-[64px] text-outline-variant">table_chart</span>
          <p className="font-headline font-bold text-xl uppercase mt-3 text-secondary">No stock groups uploaded yet</p>
          <p className="font-body text-sm text-secondary mt-1 mb-6">Upload your first Tally export to get started.</p>
          <button onClick={openUpload} className="btn-primary">Upload Excel File(s)</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {groups.map(g => (
            <div key={g._id} className="bg-white border border-outline-variant/20 flex flex-col">
              <div className="bg-surface-container-low px-5 py-5 flex items-center gap-4 border-b border-outline-variant/20">
                <div className="w-12 h-12 bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-[28px] text-green-700">table_chart</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-headline font-bold text-base uppercase tracking-tight text-on-surface leading-tight truncate">{g.groupName}</p>
                  <p className="font-label text-xs text-secondary truncate mt-0.5">{g.items.length} product{g.items.length !== 1 ? 's' : ''}</p>
                </div>
              </div>

              <div className="px-5 py-4 flex-1 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="font-label text-xs text-secondary">Last updated</span>
                  <span className="font-label text-xs font-bold text-on-surface">
                    {new Date(g.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>

                <div className="flex gap-2 pt-2 border-t border-surface-container">
                  <button onClick={() => del(g._id, g.groupName)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-outline-variant font-label text-xs font-bold text-secondary hover:border-error hover:text-error transition-colors">
                    <span className="material-symbols-outlined text-[14px]">delete</span> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container">
              <h2 className="font-headline font-black text-xl uppercase">Upload Stock Excel File(s)</h2>
              <button onClick={() => setModal(false)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="input-label">Excel File(s) (.xlsx) *</label>
                <input ref={fileRef} type="file" accept=".xlsx" multiple
                  onChange={e => setFiles(Array.from(e.target.files))}
                  className="input-field" />
                <p className="font-label text-xs text-secondary mt-1">
                  Select one or more Tally "Stock Group Summary" exports. Each will replace its own group only.
                </p>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={i} className="font-label text-xs text-primary-container">
                        {f.name} ({(f.size / 1024).toFixed(0)} KB)
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3">
              <button onClick={() => setModal(false)} className="btn-ghost">Cancel</button>
              <button onClick={handleUpload} disabled={uploading} className="btn-primary">
                {uploading
                  ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading...</>
                  : <><span className="material-symbols-outlined">upload_file</span> Upload</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
